import React, { useEffect, useMemo, useState } from "react";
import { getMaintenanceIntensity } from "../../api/work-orderAPI";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toIsoDate = (date) => date.toISOString().slice(0, 10);

const startOfWeekMondayUtc = (date) => {
  const day = date.getUTCDay();
  const diff = (day + 6) % 7;
  const start = new Date(date);
  start.setUTCDate(start.getUTCDate() - diff);
  start.setUTCHours(0, 0, 0, 0);
  return start;
};

const endOfWeekSundayUtc = (date) => {
  const day = date.getUTCDay();
  const diff = (7 - day) % 7;
  const end = new Date(date);
  end.setUTCDate(end.getUTCDate() + diff);
  end.setUTCHours(0, 0, 0, 0);
  return end;
};

const buildDateRange = (start, end) => {
  const days = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push(new Date(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
};

const getIntensityColor = (count, max) => {
  if (!count) {
    return "#e9ecef";
  }
  if (!max) {
    return "#e9ecef";
  }
  const ratio = count / max;
  if (ratio <= 0.25) {
    return "#c8e6c9";
  }
  if (ratio <= 0.5) {
    return "#81c784";
  }
  if (ratio <= 0.75) {
    return "#4caf50";
  }
  return "#2e7d32";
};

const WorkOrderMaintenanceIntensity = ({ assetId }) => {
  const [intensityMap, setIntensityMap] = useState(new Map());
  const [loading, setLoading] = useState(true);

  const { displayDays, weeks, maxCount } = useMemo(() => {
    const today = new Date();
    const utcToday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const startWindow = new Date(utcToday);
    startWindow.setUTCDate(startWindow.getUTCDate() - 364);
    const start = startOfWeekMondayUtc(startWindow);
    const end = endOfWeekSundayUtc(utcToday);
    const days = buildDateRange(start, end);
    const weeksChunked = [];
    for (let i = 0; i < days.length; i += 7) {
      weeksChunked.push(days.slice(i, i + 7));
    }
    const counts = Array.from(intensityMap.values());
    const maxValue = counts.length ? Math.max(...counts) : 0;
    return {
      displayDays: days,
      weeks: weeksChunked,
      maxCount: maxValue,
    };
  }, [intensityMap]);

  const getWeekMonth = (week) => week[week.length - 1].getUTCMonth();

  const monthLabels = useMemo(() => {
    const labels = [];
    let currentMonth = null;
    let span = 0;

    weeks.forEach((week) => {
      const month = getWeekMonth(week);
      if (month !== currentMonth) {
        if (currentMonth !== null) {
          labels.push({ month: currentMonth, span });
        }
        currentMonth = month;
        span = 1;
      } else {
        span += 1;
      }
    });

    if (currentMonth !== null) {
      labels.push({ month: currentMonth, span });
    }

    return labels;
  }, [weeks]);

  useEffect(() => {
    const useGlobalIntensity = assetId === undefined;
    const normalizedAssetId = useGlobalIntensity ? -1 : Number(assetId);
    if (!useGlobalIntensity && !Number.isFinite(normalizedAssetId)) {
      setIntensityMap(new Map());
      setLoading(false);
      return;
    }

    const fetchIntensity = async () => {
      setLoading(true);
      const today = new Date();
      const utcToday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      const startWindow = new Date(utcToday);
      startWindow.setUTCDate(startWindow.getUTCDate() - 364);
      const startDate = toIsoDate(startOfWeekMondayUtc(startWindow));
      const endDate = toIsoDate(utcToday);
      const response = await getMaintenanceIntensity({
        assetIds: [normalizedAssetId],
        startDate,
        endDate,
      });
      const assetIntensity = Array.isArray(response?.assets) ? response.assets : [];
      const counts = new Map();
      assetIntensity.forEach((asset) => {
        const entries = Array.isArray(asset?.intensity) ? asset.intensity : [];
        entries.forEach((entry) => {
          if (!entry?.weekStart) {
            return;
          }
          const current = counts.get(entry.weekStart) ?? 0;
          counts.set(entry.weekStart, current + Number(entry.workOrders || 0));
        });
      });
      setIntensityMap(counts);
      setLoading(false);
    };
    fetchIntensity();
  }, [assetId]);

  return (
    <div className="col-xxl-12">
      <div className="card h-100 radius-8 border-0">
        <div className="card-body p-24 mb-24">
          <div className="d-flex align-items-center flex-wrap gap-2 justify-content-between mb-20">
            <h6 className="mb-2 fw-bold text-lg mb-0">Maintenance Intensity</h6>
            <div className="d-flex align-items-center gap-8 text-secondary-light text-sm">
              <span>Less</span>
              <div className="d-flex align-items-center gap-4">
                {[0, 1, 2, 3, 4].map((level) => (
                  <span
                    key={level}
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      backgroundColor: getIntensityColor(level, 4),
                      border: "1px solid rgba(0,0,0,0.05)",
                      display: "inline-block",
                    }}
                  />
                ))}
              </div>
              <span>More</span>
            </div>
          </div>
          {loading ? (
            <div className="text-center text-secondary-light py-40">Loading maintenance intensity...</div>
          ) : (
            <div style={{ overflowX: "auto", height: "100%" }}>
              <div style={{ minWidth: 640 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `40px repeat(${weeks.length}, 18px)`,
                    gap: 4,
                    marginBottom: 8,
                  }}
                >
                  <div />
                  {monthLabels.map((label, index) => (
                    <div
                      key={`${label.month}-${index}`}
                      className="text-secondary-light text-xs"
                      style={{
                        gridColumn: `span ${label.span}`,
                        minWidth: 14,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {MONTHS[label.month]}
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `40px repeat(${weeks.length}, 18px)`,
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateRows: "repeat(7, 18px)",
                      gap: 4,
                      alignItems: "center",
                    }}
                  >
                    {WEEKDAYS.map((day) => (
                      <div key={day} className="text-secondary-light text-xs">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridAutoFlow: "column",
                      gridTemplateRows: "repeat(7, 18px)",
                      gap: 4,
                    }}
                  >
                    {displayDays.map((date) => {
                      const key = toIsoDate(date);
                      const count = intensityMap.get(key) ?? 0;
                      return (
                        <div
                          key={key}
                          title={`${key}: ${count} work orders`}
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 2,
                            backgroundColor: getIntensityColor(count, maxCount),
                            border: "1px solid rgba(0,0,0,0.05)",
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderMaintenanceIntensity;
